import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Node } from 'src/common/interfaces/node.interface';
import { Step } from 'src/common/interfaces/step.interface';
import { functionNoop } from 'src/utils/function/noop';

import { EditorConfirmPanel } from '../confirm-panel';

import { ContentSecurityPanel } from './security-panel';
import { SyncedStepEditPanel } from './synced-step-edit-panel';
import { SyncedStepCreatePanel } from './synced-step-create-panel';
import { useEditorContext } from 'src/store/editor';
import { apiRefreshNodeThumbnail } from 'src/utils/journeyApi';
import { useEditorNavigator } from '../use-editor-navigator.hook';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { Nullable } from 'src/types/nullable.type';
import { EditorContentCardOptionsMenu, EditorContentCardOptionsMenuItem } from './card-options-menu';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { ContentCopyUrlToStepPanel } from './copy-url-to-step-panel';

import {
  ChevronRightRegularIcon,
  DeleteRegularIcon,
  EditRegularIcon,
  LinkRegularIcon,
  SecureRegularIcon,
  SyncRegularIcon,
  FrameImageRegularIcon,
} from 'src/monet/icons';
import { removeNodeFromCurrentJourney } from '../api';
import { EventBus } from 'src/common/EventBus';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { useStepEditorContext } from 'src/store/step-editor';

export type EditorContentActivePanelTypes = 'delete' | 'security' | 'sync-step' | 'edit' | 'copy' | 'refresh-thumbnail';

interface EditorContentCardOptionsProps extends Pick<JourneyMenuPropsInterface, 'offset' | 'placement'> {
  node: Node;
  step: Step;
  nestedMenuOffset?: JourneyMenuPropsInterface['offset'];
  nestedMenuPlacement?: JourneyMenuPropsInterface['placement'];
  onMenuOpen?: () => void;
  activePanelRef?: MutableRefObject<Nullable<EditorContentActivePanelTypes>>;
  onMenuClose?: () => void;
  onDeleteSuccess?: () => void;
  showEdit?: boolean;
  showDelete?: boolean;
  renderMenuOrigin: (onClick: () => void) => any;
}

export const EditorContentCardOptions: FC<EditorContentCardOptionsProps> = ({
  node,
  step,
  activePanelRef,
  onMenuOpen = functionNoop,
  onMenuClose = functionNoop,
  onDeleteSuccess = functionNoop,
  showDelete = true,
  nestedMenuPlacement,
  showEdit = true,
  placement,
  offset,
  nestedMenuOffset,
  renderMenuOrigin,
}) => {
  const innerPanelRef = useRef<Nullable<EditorContentActivePanelTypes>>(null);
  const settingsMenuOpenRef = useRef(functionNoop);
  const settingsMenuCloseRef = useRef(functionNoop);

  const deletePanelOpenRef = useRef(functionNoop);
  const deletePanelCloseRef = useRef(functionNoop);

  const securityPanelOpenRef = useRef(functionNoop);
  const securityPanelCloseRef = useRef(functionNoop);

  const syncStepPanelOpenRef = useRef(functionNoop);
  const syncStepPanelCloseRef = useRef(functionNoop);

  const copyUrlToStepPanelOpenRef = useRef(functionNoop);
  const copyUrlToStepPanelCloseRef = useRef(functionNoop);

  const { setStateFromNode, setPreviewObject } = useStepEditorContext();
  const { setJourney, updateNodeInJourney } = useEditorContext();
  const { navigateToContentEdit } = useEditorNavigator();

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [activePanel, setActivePanel] = useState<Nullable<EditorContentActivePanelTypes>>(null);

  const currentPanelRef = useMemo(() => activePanelRef || innerPanelRef, [activePanelRef]);

  const onMenuClicked = () => {
    settingsMenuOpenRef.current();
  };

  useEffect(() => {
    settingsMenuOpenRef.current();
  }, []);

  useEffect(() => {
    if (activePanel === 'edit') {
      navigateToContentEdit(step.id, node.id);
    }
  }, [activePanel, navigateToContentEdit, node, step]);

  const onBack = () => {
    currentPanelRef.current = null;
    setActivePanel(null);
    deletePanelCloseRef.current();
    securityPanelCloseRef.current();
    syncStepPanelCloseRef.current();
    copyUrlToStepPanelCloseRef.current();

    setTimeout(onMenuClicked, 50);
  };

  const onClosePanel = useCallback(() => {
    currentPanelRef.current = null;
    setActivePanel(null);
    onMenuClose();
  }, [onMenuClose, currentPanelRef]);

  const onDelete = async (node: any, step: any) => {
    try {
      const journey = await removeNodeFromCurrentJourney(node.uuid, step.id);
      setSuccessNotification('Step successfully deleted.');
      onDeleteSuccess();
      setJourney(journey);
    } catch (error) {
      console.error(error);
      setErrorNotification((error as ErrorInterface).message);
    }
  };

  const menuOptions = useMemo(() => {
    let options: EditorContentCardOptionsMenuItem[] = [];
    if (showEdit) {
      options.push({
        type: 'edit',
        render: () => (
          <>
            <EditRegularIcon />
            <span>Edit</span>
          </>
        ),
        onClick: () => {
          setStateFromNode(node);
          navigateToContentEdit(step.id, node.id);
        },
      });
    }
    if (showDelete) {
      options.push({
        type: 'delete',
        render: () => (
          <>
            <DeleteRegularIcon />
            <span>Delete</span>
          </>
        ),
        onClick: () => {
          currentPanelRef.current = 'delete';
          setActivePanel('delete');
        },
      });
    }
    options.push({
      type: 'copy',
      render: () => {
        return (
          <>
            <LinkRegularIcon />
            <span>Copy URL to this Step</span>
            <span className='!ml-auto'>
              <ChevronRightRegularIcon />
            </span>
          </>
        );
      },
      onClick: (e) => {
        currentPanelRef.current = 'copy';
        setActivePanel('copy');
      },
    });

    options.push({
      type: 'security',
      render: () => (
        <>
          <SecureRegularIcon />
          <span>Security</span>
          <span className='!ml-auto'>
            <ChevronRightRegularIcon />
          </span>
        </>
      ),
      onClick: () => {
        currentPanelRef.current = 'security';
        setActivePanel('security');
      },
    });

    options.push({
      type: 'sync-step',
      render: () => (
        <>
          <SyncRegularIcon />
          <span>Sync Step</span>
          <span className='!ml-auto'>
            <ChevronRightRegularIcon />
          </span>
        </>
      ),
      onClick: () => {
        currentPanelRef.current = 'sync-step';
        setActivePanel('sync-step');
      },
    });

    options.push({
      type: 'refresh-thumbnail',
      render: () => (
        <>
          <FrameImageRegularIcon />
          <span>Refresh thumbnail</span>
        </>
      ),
      onClick: () => {
        setSuccessNotification('New thumbnail being created.');
        EventBus.emit('step:reset-thumbnail-start', node);
        apiRefreshNodeThumbnail(node.id)
          .then((nodeResponse) => {
            updateNodeInJourney(nodeResponse, step);
            setSuccessNotification('Successfully regenerated thumbnail.');
            EventBus.emit('step:reset-thumbnail-end', node);
          })
          .catch(() => {
            setErrorNotification('Thumbnail could not be refreshed.');
            EventBus.emit('step:reset-thumbnail-end', node);
          });
      },
    });

    return options;
  }, [node, navigateToContentEdit]);

  if (activePanel === 'delete') {
    return (
      <EditorConfirmPanel
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        containerClassName='w-60'
        offset={nestedMenuOffset || offset}
        placement={nestedMenuPlacement || placement}
        openRef={deletePanelOpenRef}
        closeRef={deletePanelCloseRef}
        copy='Deleting this Step is an irreversible action'
        confirmCopy='Yes, delete'
        denyCopy='No, keep'
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        onConfirm={() => onDelete(node, step)}
        onDeny={onClosePanel}
        onBack={onBack}
      />
    );
  }

  if (activePanel === 'security') {
    return (
      <ContentSecurityPanel
        step={step}
        node={node}
        offset={nestedMenuOffset || offset}
        placement={nestedMenuPlacement || placement}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        openRef={securityPanelOpenRef}
        closeRef={securityPanelCloseRef}
        onBack={onBack}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
      />
    );
  }

  if (activePanel === 'sync-step') {
    if (node.is_synced) {
      return (
        <SyncedStepEditPanel
          node={node}
          step={step}
          offset={nestedMenuOffset || offset}
          placement={nestedMenuPlacement || placement}
          renderOrigin={() => renderMenuOrigin(onMenuClicked)}
          openRef={syncStepPanelOpenRef}
          closeRef={syncStepPanelCloseRef}
          onBack={onBack}
          onOpen={onMenuOpen}
          onClose={onClosePanel}
        />
      );
    } else {
      return (
        <SyncedStepCreatePanel
          step={step}
          node={node}
          offset={nestedMenuOffset || offset}
          placement={nestedMenuPlacement || placement}
          renderOrigin={() => renderMenuOrigin(onMenuClicked)}
          openRef={syncStepPanelOpenRef}
          closeRef={syncStepPanelCloseRef}
          onBack={onBack}
          onOpen={onMenuOpen}
          onClose={onClosePanel}
        />
      );
    }
  }

  if (activePanel === 'copy') {
    return (
      <ContentCopyUrlToStepPanel
        node={node}
        offset={nestedMenuOffset || offset}
        placement={nestedMenuPlacement || placement}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        openRef={copyUrlToStepPanelOpenRef}
        closeRef={copyUrlToStepPanelCloseRef}
        onBack={onBack}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
      />
    );
  }

  return (
    <EditorContentCardOptionsMenu
      openRef={settingsMenuOpenRef}
      closeRef={settingsMenuCloseRef}
      onOpen={onMenuOpen}
      offset={offset}
      placement={nestedMenuPlacement || placement}
      onClose={onMenuClose}
      renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      items={menuOptions}
    />
  );
};
